/**
 * TanStack Query Hooks for Tenant Reports
 *
 * This module provides hooks for managing tenant reports including:
 * - Fetching reports with pagination and filters
 * - Creating new reports
 * - Fetching report details
 * - Activating/Deactivating reports
 * - Sending reports
 * - Public report access
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";

import {
  activateReport,
  addGuarantorInvestigation,
  addReportPerson,
  createTenantReport,
  deactivateReport,
  getTenantMultireportPublicData,
  getTenantMultireportPublicLink,
  getTenantMultireportWebData,
  getTenantReportDetails,
  getTenantReports,
  getTenantReportsByPropertyId,
  sendTenantMultireport,
  sendTenantMultireportExpress,
} from "api/tenants";

import { getTenantReportErrorMessage } from "js/constants/tenant-report-errors";

import { toast } from "../utils/toast";
import { queryKeys } from "./queryKeys";

type TenantReportEntity = {
  id: string;
  is_active: boolean;
  [key: string]: unknown;
};

type TenantReportListResponse = {
  results?: TenantReportEntity[];
  [key: string]: unknown;
};

type StoredListQuery = [
  ReadonlyArray<unknown>,
  TenantReportListResponse | undefined,
];

type TenantReportMutationContext = {
  tenantLists: StoredListQuery[];
  organizationLists: StoredListQuery[];
  detail?: TenantReportEntity;
  detailKey: ReturnType<typeof queryKeys.tenantReports.detail>;
  detailMutated: boolean;
};

const updateListIsActive = (
  data: TenantReportListResponse | undefined,
  reportId: string,
  isActive: boolean,
): { data: TenantReportListResponse | undefined; didChange: boolean } => {
  if (!data?.results?.length) {
    return { data, didChange: false };
  }

  let didChange = false;
  const results = data.results.map((report) => {
    if (report.id === reportId && report.is_active !== isActive) {
      didChange = true;
      return { ...report, is_active: isActive };
    }
    return report;
  });

  if (!didChange) {
    return { data, didChange: false };
  }

  return {
    data: { ...data, results } as TenantReportListResponse,
    didChange: true,
  };
};

const updateDetailIsActive = (
  data: TenantReportEntity | undefined,
  reportId: string,
  isActive: boolean,
): { data: TenantReportEntity | undefined; didChange: boolean } => {
  if (!data || data.id !== reportId || data.is_active === isActive) {
    return { data, didChange: false };
  }

  return {
    data: { ...data, is_active: isActive },
    didChange: true,
  };
};

const applyOptimisticIsActive = (
  queryClient: QueryClient,
  reportId: string,
  isActive: boolean,
): TenantReportMutationContext => {
  const tenantLists = queryClient.getQueriesData<TenantReportListResponse>({
    queryKey: queryKeys.tenantReports.lists(),
  });
  const organizationLists =
    queryClient.getQueriesData<TenantReportListResponse>({
      queryKey: queryKeys.organization.tenantReports.all(),
    });

  const detailKey = queryKeys.tenantReports.detail(reportId);
  const detail = queryClient.getQueryData<TenantReportEntity>(detailKey);

  tenantLists.forEach(([key, listData]) => {
    const { data, didChange } = updateListIsActive(
      listData,
      reportId,
      isActive,
    );
    if (didChange) {
      queryClient.setQueryData(key, data);
    }
  });

  organizationLists.forEach(([key, listData]) => {
    const { data, didChange } = updateListIsActive(
      listData,
      reportId,
      isActive,
    );
    if (didChange) {
      queryClient.setQueryData(key, data);
    }
  });

  const detailUpdate = updateDetailIsActive(detail, reportId, isActive);
  if (detailUpdate.didChange && detailUpdate.data) {
    queryClient.setQueryData(detailKey, detailUpdate.data);
  }

  return {
    tenantLists,
    organizationLists,
    detail,
    detailKey,
    detailMutated: detailUpdate.didChange,
  };
};

const prepareOptimisticToggle = async (
  queryClient: QueryClient,
  reportId: string,
  isActive: boolean,
) => {
  await Promise.all([
    queryClient.cancelQueries({
      queryKey: queryKeys.tenantReports.lists(),
    }),
    queryClient.cancelQueries({
      queryKey: queryKeys.organization.tenantReports.all(),
    }),
    queryClient.cancelQueries({
      queryKey: queryKeys.tenantReports.detail(reportId),
      exact: true,
    }),
  ]);

  return applyOptimisticIsActive(queryClient, reportId, isActive);
};

const rollbackOptimisticToggle = (
  queryClient: QueryClient,
  context?: TenantReportMutationContext,
) => {
  if (!context) {
    return;
  }

  context.tenantLists.forEach(([key, data]) => {
    queryClient.setQueryData(key, data);
  });

  context.organizationLists.forEach(([key, data]) => {
    queryClient.setQueryData(key, data);
  });

  if (context.detailMutated && context.detail) {
    queryClient.setQueryData(context.detailKey, context.detail);
  }
};

// ============================================================================
// QUERY HOOKS - Fetching data
// ============================================================================

/**
 * Get all tenant reports with pagination and filters
 */
export const useGetTenantReports = (
  pagination: { pageSize: number; current: number },
  filter: string = "",
  additionalFilters: {
    is_active?: string;
    status?: string;
    legal_type?: string;
  } = {},
) => {
  return useQuery({
    queryKey: queryKeys.tenantReports.list({
      pagination,
      filter,
      additionalFilters,
    }),
    queryFn: () => getTenantReports(pagination, filter, additionalFilters),
    staleTime: 30000, // 30 seconds - reports change moderately
  });
};

/**
 * Get tenant reports by property ID
 */
export const useGetTenantReportsByPropertyId = (
  pagination: { pageSize: number; current: number },
  propertyId: string,
  options?: { enabled?: boolean; organizationScope?: boolean },
) => {
  const organizationScope = options?.organizationScope ?? false;

  return useQuery({
    queryKey: [
      ...queryKeys.tenantReports.byProperty(propertyId),
      {
        page: pagination.current,
        pageSize: pagination.pageSize,
        organizationScope,
      },
    ],
    queryFn: () =>
      getTenantReportsByPropertyId(pagination, propertyId, organizationScope),
    enabled: options?.enabled ?? !!propertyId,
    staleTime: 30000,
  });
};

/**
 * Get detailed information about a specific tenant report
 */
export const useGetTenantReportDetails = (
  reportId: string,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: queryKeys.tenantReports.detail(reportId),
    queryFn: () => getTenantReportDetails(reportId),
    enabled: options?.enabled ?? !!reportId,
    staleTime: 60000, // 1 minute - detail data changes less frequently
    // Auto-refresh: re-fetch every time the user comes back to the tab or re-enters
    // the page, so the report reflects the latest multiexpediente state without a
    // full page reload. "always" ignores staleTime for these triggers.
    refetchOnWindowFocus: "always",
    refetchOnMount: "always",
  });
};

/**
 * Get public link for a tenant multireport
 */
export const useGetTenantMultireportPublicLink = (
  reportId: string,
  regenerate: boolean = false,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: queryKeys.tenantReports.publicLink(reportId),
    queryFn: () => getTenantMultireportPublicLink(reportId, regenerate),
    enabled: options?.enabled ?? !!reportId,
    staleTime: regenerate ? 0 : 300000, // 5 minutes (or 0 if regenerating)
  });
};

/**
 * Get tenant multireport web data
 */
export const useGetTenantMultireportWebData = (
  reportId: string,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: queryKeys.tenantReports.webData(reportId),
    queryFn: () => getTenantMultireportWebData(reportId),
    enabled: options?.enabled ?? !!reportId,
    staleTime: 60000,
  });
};

/**
 * Get public tenant multireport data (no auth required)
 */
export const useGetTenantMultireportPublicData = (
  token: string,
  email?: string,
  options?: { enabled?: boolean; nonce?: number },
) => {
  return useQuery({
    // email is part of the key so different emails are distinct queries (and a
    // stale-closure refetch can't fire with the wrong email); nonce lets the
    // same email be retried after a transient error.
    queryKey: [
      ...queryKeys.tenantReports.publicData(token),
      email ?? "",
      options?.nonce ?? 0,
    ],
    queryFn: () => getTenantMultireportPublicData(token, email),
    enabled: options?.enabled ?? !!token,
    staleTime: 300000, // 5 minutes - public data
  });
};

// ============================================================================
// MUTATION HOOKS - Creating/Updating data
// ============================================================================

/**
 * Create a new tenant report
 * Invalidates: tenant reports lists, tenants lists, organization credits
 */
export const useCreateTenantReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: any) => createTenantReport(values),
    onSuccess: () => {
      toast.success("Reporte de inquilino creado exitosamente");
      // Invalidate tenant reports
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantReports.all });
      // Invalidate tenants (status may change)
      queryClient.invalidateQueries({ queryKey: queryKeys.tenants.all });
      // Invalidate organization data (credits consumed)
      queryClient.invalidateQueries({ queryKey: queryKeys.organization.all });
    },
    onError: (error: unknown) => {
      toast.error(
        getTenantReportErrorMessage(error, "Error al crear el reporte."),
      );
    },
  });
};

/**
 * Activate a tenant report
 * Invalidates: tenant reports lists and specific report
 */
export const useActivateTenantReport = () => {
  const queryClient = useQueryClient();

  return useMutation<unknown, any, string, TenantReportMutationContext>({
    mutationFn: (reportId: string) => activateReport(reportId),
    onMutate: async (reportId) =>
      prepareOptimisticToggle(queryClient, reportId, true),
    onSuccess: (_, reportId) => {
      toast.success("Reporte activado exitosamente");
      // Invalidate report lists
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantReports.lists(),
      });
      // Invalidate specific report
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantReports.detail(reportId),
      });
      // Invalidate organization views
      queryClient.invalidateQueries({
        queryKey: queryKeys.organization.tenantReports.all(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.organization.reports(),
      });
    },
    onError: (error: unknown, _reportId, context) => {
      rollbackOptimisticToggle(queryClient, context);
      toast.error(
        getTenantReportErrorMessage(error, "Error al activar el reporte."),
      );
    },
  });
};

/**
 * Deactivate a tenant report
 * Invalidates: tenant reports lists and specific report
 */
export const useDeactivateTenantReport = () => {
  const queryClient = useQueryClient();

  return useMutation<unknown, any, string, TenantReportMutationContext>({
    mutationFn: (reportId: string) => deactivateReport(reportId),
    onMutate: async (reportId) =>
      prepareOptimisticToggle(queryClient, reportId, false),
    onSuccess: (_, reportId) => {
      toast.success("Reporte desactivado exitosamente");
      // Invalidate report lists
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantReports.lists(),
      });
      // Invalidate specific report
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantReports.detail(reportId),
      });
      // Invalidate organization views
      queryClient.invalidateQueries({
        queryKey: queryKeys.organization.tenantReports.all(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.organization.reports(),
      });
    },
    onError: (error: unknown, _reportId, context) => {
      rollbackOptimisticToggle(queryClient, context);
      toast.error(
        getTenantReportErrorMessage(error, "Error al desactivar el reporte."),
      );
    },
  });
};

/**
 * Send tenant multireport via email
 * Invalidates: specific report details (may update sent status)
 */
export const useSendTenantMultireport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reportId: string) => sendTenantMultireport(reportId),
    onSuccess: (_, reportId) => {
      toast.success("Multireporte enviado exitosamente");
      // Invalidate specific report details
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantReports.detail(reportId),
      });
    },
    onError: (error: unknown) => {
      toast.error(
        getTenantReportErrorMessage(error, "Error al enviar el multireporte."),
      );
    },
  });
};

/**
 * Send tenant multireport express via email
 * Invalidates: specific report details (may update sent status)
 */
export const useSendTenantMultireportExpress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reportId: string) => sendTenantMultireportExpress(reportId),
    onSuccess: (_, reportId) => {
      toast.success("Multireporte express enviado exitosamente");
      // Invalidate specific report details
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantReports.detail(reportId),
      });
    },
    onError: (error: unknown) => {
      toast.error(
        getTenantReportErrorMessage(
          error,
          "Error al enviar el multireporte express.",
        ),
      );
    },
  });
};

/**
 * Add guarantor investigation to an existing report
 * Redirects to payment page on success
 */
export const useAddGuarantorInvestigation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reportId: string) => addGuarantorInvestigation(reportId),
    onSuccess: (_data, reportId) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantReports.detail(reportId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantReports.lists(),
      });
    },
    onError: (error: unknown) => {
      toast.error(
        getTenantReportErrorMessage(
          error,
          "Error al agregar investigación del fiador.",
        ),
      );
    },
  });
};

/**
 * Add an additional participant (tenant / guarantor / landlord) to a report.
 */
export const useAddReportPerson = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      reportId,
      payload,
    }: {
      reportId: string;
      payload: {
        role: "tenant" | "guarantor" | "landlord";
        person: Record<string, any>;
      };
    }) => addReportPerson(reportId, payload),
    onSuccess: (_data, { reportId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantReports.detail(reportId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantReports.lists(),
      });
    },
    onError: (error: unknown) => {
      toast.error(
        getTenantReportErrorMessage(error, "Error al agregar la persona."),
      );
    },
  });
};

/**
 * Generate public link for tenant multireport
 * Invalidates: public link data, report details
 */
export const useGenerateTenantMultireportPublicLink = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      reportId,
      regenerate = true,
    }: {
      reportId: string;
      regenerate?: boolean;
    }) => getTenantMultireportPublicLink(reportId, regenerate),
    onSuccess: (data, variables) => {
      toast.success("Enlace público generado exitosamente");
      // Invalidate public link and report details
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantReports.publicLink(variables.reportId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantReports.detail(variables.reportId),
      });
    },
    onError: (error: unknown) => {
      toast.error(
        getTenantReportErrorMessage(
          error,
          "Error al generar el enlace público.",
        ),
      );
    },
  });
};
